import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { db, subscribeToPush, getExistingSubscription, auth } from "@/config/firebase";
import { collection, getDoc, getDocs, query, where, doc as docRef, setDoc, onSnapshot, Timestamp, serverTimestamp } from "firebase/firestore";
import { APP_VERSION, APP_BUILD_DATE } from "@/version";
import { useNavigate } from "react-router-dom";
import { CalendarDays, Beer, BarChart3, Trophy, MessageSquare, Image, Settings, ChevronRight, Users, Clock, MapPin, CheckCircle2, XCircle, Bell, BellOff, Download, Gamepad2, BookOpen, Medal, Loader2, ExternalLink, Shuffle, Check, X, Sparkles, Film, Flame, MoreHorizontal } from "lucide-react";
import TeamBuilder from "@/components/TeamBuilder";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const GAME_ROLES = [
  { key: 'play', label: 'Spielen', icon: '🏀' },
  { key: 'cheer', label: 'Anfeuern', icon: '📣' },
  { key: 'table', label: 'Tisch', icon: '📋', max: 3, homeOnly: true },
  { key: 'coach', label: 'Coach', icon: '🎯', max: 1 },
  { key: 'trainer_bag', label: 'Tasche', icon: '🎒', max: 1, homeOnly: true },
  { key: 'jerseys', label: 'Trikots', icon: '👕', max: 1 },
  { key: 'passes', label: 'Pässe', icon: '🎫', max: 1, awayOnly: true },
  { key: 'keys', label: 'Schlüssel', icon: '🔑', max: 1, homeOnly: true },
];

function parseDate(v) {
  if (v?.toDate) return v.toDate();
  if (typeof v === 'string') return new Date(v);
  return null;
}

export default function Dashboard() {
  const { user, role, logout } = useAuth();
  const navigate = useNavigate();
  const [meDoc, setMeDoc] = useState(null);
  const [users, setUsers] = useState([]);
  const [nextEvents, setNextEvents] = useState([]);
  const [eventsSignups, setEventsSignups] = useState({});
  const [activeKioskSession, setActiveKioskSession] = useState(null);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [expandedEvent, setExpandedEvent] = useState(null);
  const [showGuestModal, setShowGuestModal] = useState(null); // eventId or null
  const [guestName, setGuestName] = useState('');
  const [showTeamBuilder, setShowTeamBuilder] = useState(null); // eventId or null
  const [showMore, setShowMore] = useState(false);
  const [notificationsEnabled, setNotificationsEnabled] = useState(() => {
    if ('Notification' in window) {
      return Notification.permission === 'granted';
    }
    return false;
  });
  const [notificationLoading, setNotificationLoading] = useState(false);
  const [canInstallChat, setCanInstallChat] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallModal, setShowInstallModal] = useState(false);
  const [installInstructions, setInstallInstructions] = useState('');
  const [chatUrlForModal, setChatUrlForModal] = useState('');
  const [trainingGames, setTrainingGames] = useState({}); // eventId -> games[]

  // Listen for PWA install prompt
  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setCanInstallChat(true);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const toggleNotifications = async () => {
    if (!('Notification' in window)) {
      alert('❌ Dein Browser unterstützt keine Benachrichtigungen.\n\nBitte nutze Chrome, Firefox oder Safari.');
      return;
    }
    
    // Check if service workers are available (may be disabled in preview)
    if (!('serviceWorker' in navigator)) {
      alert('⚠️ Service Worker nicht verfügbar.\n\nDies kann an der Preview-Umgebung liegen. Push-Notifications funktionieren nach dem Deployment auf die echte Domain.');
      return;
    }
    
    if (!('PushManager' in window)) {
      alert('❌ Push-Benachrichtigungen werden von diesem Browser nicht unterstützt.');
      return;
    }
    
    if (Notification.permission === 'denied') {
      alert('⚠️ Notifications wurden blockiert.\n\nSo aktivierst du sie:\n\n1. Klicke auf das Schloss-Symbol links in der Adressleiste\n2. Finde "Benachrichtigungen"\n3. Ändere auf "Zulassen"\n4. Lade die Seite neu');
      return;
    }
    
    setNotificationLoading(true);
    
    try {
      // First request notification permission
      const permission = await Notification.requestPermission();
      
      if (permission !== 'granted') {
        alert('❌ Berechtigung wurde nicht erteilt.\n\nOhne Berechtigung können keine Notifications angezeigt werden.');
        setNotificationLoading(false);
        return;
      }
      
      // Try to register service worker
      let registration;
      try {
        registration = await navigator.serviceWorker.register('/sw-chat.js');
        console.log('Service worker registered:', registration);
      } catch (swError) {
        console.warn('Service worker registration failed:', swError);
        // In preview, SW might be blocked - still show success for permission
        setNotificationsEnabled(true);
        new Notification('✅ Browser-Benachrichtigungen aktiviert!', {
          body: 'Push-Notifications werden nach Deployment verfügbar sein.',
          icon: '/icon-192.png'
        });
        setNotificationLoading(false);
        return;
      }
      
      // Wait for service worker to be ready
      await navigator.serviceWorker.ready;
      
      // Subscribe to push notifications
      const subscription = await subscribeToPush();
      
      console.log('[PUSH] Subscription result:', subscription);
      
      if (subscription && subscription.endpoint && subscription.keys && user) {
        const userName = name || user.displayName || user.email?.split('@')[0] || 'User';
        
        console.log('[PUSH] Sending subscription to backend...');
        
        // Send subscription to backend
        const response = await fetch(`${BACKEND_URL}/api/push/subscribe`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            user_id: user.uid,
            user_name: userName,
            endpoint: subscription.endpoint,
            keys: subscription.keys
          })
        });
        
        if (response.ok) {
          console.log('[PUSH] Subscription saved successfully!');
          setNotificationsEnabled(true);
          localStorage.setItem('rs5_push_user_id', user.uid);
          new Notification('🔔 Push-Benachrichtigungen aktiviert!', {
            body: 'Du wirst jetzt über neue Chat-Nachrichten informiert.',
            icon: '/icon-192.png'
          });
        } else {
          const errorData = await response.json().catch(() => ({}));
          console.error('[PUSH] Failed to register push subscription:', errorData);
          alert('Fehler beim Registrieren. Bitte versuche es erneut.');
        }
      } else if (Notification.permission === 'granted') {
        // Permission granted but subscription failed - common in preview/localhost
        console.warn('[PUSH] Permission granted but subscription failed. This may work after deployment.');
        setNotificationsEnabled(true);
        new Notification('✅ Browser-Benachrichtigungen aktiviert!', {
          body: 'Push-Notifications funktionieren nach dem Deployment.',
          icon: '/icon-192.png'
        });
      } else {
        console.error('[PUSH] Subscription failed and permission not granted');
        alert('Push-Benachrichtigungen konnten nicht aktiviert werden.');
      }
    } catch (err) {
      console.error('Notification setup error:', err);
      
      // If it's just a SW issue in preview, still mark as enabled
      if (Notification.permission === 'granted') {
        setNotificationsEnabled(true);
        alert('✅ Browser-Berechtigung erteilt!\n\nVollständige Push-Notifications funktionieren nach dem Deployment.');
      } else {
        alert('Fehler beim Aktivieren:\n' + err.message);
      }
    }
    
    setNotificationLoading(false);
  };

  const testPushNotification = async () => {
    if (!user) {
      alert('Bitte erst einloggen');
      return;
    }
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/push/test`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: user.uid,
          message: '🏀 Test Push vom RS5 Chat!'
        })
      });
      
      if (response.ok) {
        alert('✅ Test-Push gesendet!\n\nDu solltest jetzt eine Notification auf deinem Gerät erhalten.');
      } else {
        const error = await response.json().catch(() => ({}));
        alert('❌ Push fehlgeschlagen:\n' + (error.detail || 'Unbekannter Fehler'));
      }
    } catch (err) {
      console.error('Test push error:', err);
      alert('❌ Fehler beim Senden:\n' + err.message);
    }
  };

  const installChatShortcut = async () => {
    // Chat-Shortcut kann IMMER installiert werden (separate PWA)
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isAndroid = /Android/.test(navigator.userAgent);
    
    // Dynamic URL - works in preview AND production
    const chatUrl = window.location.origin + '/chat.html';
    
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setCanInstallChat(false);
        setDeferredPrompt(null);
      }
    } else {
      // Show modal with instructions - store chatUrl for the modal
      setChatUrlForModal(chatUrl);
      let instructions = '';
      if (isIOS) {
        instructions = `📱 Chat-Icon für iPhone/iPad:

1. Tippe unten auf "Chat öffnen"

2. Tippe auf das Teilen-Symbol
   (Quadrat mit Pfeil, unten)

3. Scrolle und tippe:
   "Zum Home-Bildschirm"

4. Tippe "Hinzufügen"

✅ "RS5 Chat" erscheint auf deinem Homescreen!`;
      } else if (isAndroid) {
        instructions = `📱 Chat-Icon für Android:

1. Tippe unten auf "Chat öffnen"

2. Tippe auf ⋮ (drei Punkte oben)

3. Wähle "Zum Startbildschirm"
   oder "App installieren"

✅ "RS5 Chat" erscheint auf deinem Homescreen!`;
      } else {
        instructions = `💻 Chat-Icon für Desktop:

1. Tippe unten auf "Chat öffnen"

2. Klicke auf das ⊕ Symbol
   in der Adressleiste

3. Oder: Menü → "Installieren"

✅ "RS5 Chat" wird installiert!`;
      }
      setInstallInstructions(instructions);
      setShowInstallModal(true);
    }
  };

  const name = useMemo(() => {
    if (meDoc?.display_name) return meDoc.display_name;
    if (user?.displayName) return user.displayName;
    if (meDoc?.name) return meDoc.name;
    return user?.email?.split("@")[0] || "Spieler";
  }, [user, meDoc]);

  // Verify push subscription status on mount (after name is defined)
  useEffect(() => {
    const verifyPushSubscription = async () => {
      if (!user || !('serviceWorker' in navigator) || !('PushManager' in window)) return;
      
      try {
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();
        
        if (subscription) {
          // Subscription exists locally - verify it's saved on backend
          console.log('[PUSH] Local subscription found, verifying backend...');
          
          const response = await fetch(`${BACKEND_URL}/api/debug/push_subscriptions`);
          if (response.ok) {
            const data = await response.json();
            const userSub = data.subscriptions?.find(s => s.user_id === user.uid);
            
            if (!userSub) {
              // Subscription exists locally but not on backend - re-register
              console.log('[PUSH] Subscription missing on backend, re-registering...');
              const subData = subscription.toJSON();
              const userName = name || user.displayName || user.email?.split('@')[0] || 'User';
              
              await fetch(`${BACKEND_URL}/api/push/subscribe`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  user_id: user.uid,
                  user_name: userName,
                  endpoint: subData.endpoint,
                  keys: {
                    p256dh: subData.keys.p256dh,
                    auth: subData.keys.auth
                  }
                })
              });
              console.log('[PUSH] Re-registered subscription on backend');
            } else {
              console.log('[PUSH] Subscription verified on backend');
            }
          }
        } else {
          console.log('[PUSH] No local subscription found');
        }
      } catch (error) {
        console.warn('[PUSH] Error verifying subscription:', error);
      }
    };
    
    if (notificationsEnabled && user) {
      verifyPushSubscription();
    }
  }, [user, notificationsEnabled, name]);

  // Load user data
  useEffect(() => {
    if (!user) return;
    (async () => {
      const doc = await getDoc(docRef(db, "users", user.uid));
      setMeDoc(doc.exists() ? doc.data() : null);
      const us = await getDocs(collection(db, "users"));
      setUsers(us.docs.map(d => ({ id: d.id, ...d.data() })));
    })();
  }, [user]);

  // Load next events via Backend API (more reliable)
  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        // Try backend API first
        const response = await fetch(`${BACKEND_URL}/api/debug/events`);
        const data = await response.json();
        
        if (data.ok || data.events) {
          const events = data.events || [];
          const now = new Date();
          
          // Parse dates and filter upcoming
          const parsed = events.map(e => {
            let dt = null;
            const dtStr = e.start_datetime;
            if (dtStr) {
              try {
                if (dtStr.includes('T') && dtStr.endsWith('Z')) {
                  dt = new Date(dtStr);
                } else if (dtStr.includes('+')) {
                  dt = new Date(dtStr.split('+')[0]);
                } else {
                  dt = new Date(dtStr);
                }
              } catch { dt = null; }
            }
            return { ...e, _dt: dt };
          }).filter(e => e._dt && !isNaN(e._dt.getTime()));
          
          const upcoming = parsed.filter(e => e._dt >= now).sort((a, b) => a._dt - b._dt).slice(0, 2);
          console.log('[Dashboard] Upcoming events:', upcoming.length);
          setNextEvents(upcoming);
          
          // Load signups for each event
          const signupsMap = {};
          for (const ev of upcoming) {
            try {
              const su = await getDocs(query(collection(db, 'event_signups'), where('event_id', '==', ev.id)));
              signupsMap[ev.id] = su.docs.map(d => ({ id: d.id, ...d.data() }));
            } catch (e) {
              console.log('[Dashboard] Could not load signups for', ev.id);
              signupsMap[ev.id] = [];
            }
          }
          setEventsSignups(signupsMap);
        }
      } catch (err) {
        console.error('[Dashboard] Error loading events:', err);
        // Fallback to direct Firestore access
        try {
          const evs = await getDocs(collection(db, 'events'));
          const list = evs.docs.map(d => ({ id: d.id, ...d.data(), _dt: parseDate(d.data().start_datetime) })).filter(e => e._dt);
          const now = new Date();
          const upcoming = list.filter(e => e._dt >= now).sort((a, b) => a._dt - b._dt).slice(0, 2);
          setNextEvents(upcoming);
        } catch (e) {
          console.error('[Dashboard] Firestore fallback also failed:', e);
        }
      }
    })();
  }, [user]);

  // Listen to active kiosk session
  useEffect(() => {
    if (!user) return;
    const unsub = onSnapshot(collection(db, "kiosk_sessions"), (snap) => {
      const items = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      const active = items.find(s => !s.end_time);
      setActiveKioskSession(active || null);
    });
    return () => unsub();
  }, [user]);

  // Listen to unread chat messages
  useEffect(() => {
    if (!user) return;
    const unsub = onSnapshot(
      query(collection(db, "team_chat"), where("created_at", ">", Timestamp.fromDate(new Date(Date.now() - 86400000)))),
      (snap) => {
        const messages = snap.docs.map(d => d.data());
        const unread = messages.filter(m => m.sender_id !== user.uid && !m.read_by?.includes(user.uid)).length;
        setUnreadMessages(unread);
      }
    );
    return () => unsub();
  }, [user]);

  const getUserName = (uid) => {
    const u = users.find(x => (x.auth_uid || x.id) === uid);
    return u?.display_name || u?.name || 'Unbekannt';
  };

  // Get shortened name with last name initial (e.g., "Guido T.")
  const getShortUserName = (uid) => {
    const fullName = getUserName(uid);
    if (!fullName || fullName === 'Unbekannt') return '?';
    const parts = fullName.trim().split(' ');
    if (parts.length === 1) return parts[0];
    const firstName = parts[0];
    const lastInitial = parts[parts.length - 1].charAt(0).toUpperCase();
    return `${firstName} ${lastInitial}.`;
  };

  const toggleRole = async (eventId, roleKey) => {
    if (!user || !eventId) return;
    const docId = `${eventId}_${user.uid}`;
    const ref = docRef(db, 'event_signups', docId);
    const signups = eventsSignups[eventId] || [];
    const mine = signups.find(s => s.id === docId) || { roles: [], user_id: user.uid, event_id: eventId };
    let roles = new Set(mine.roles || []);
    
    if (roles.has(roleKey)) {
      roles.delete(roleKey);
    } else {
      roles.add(roleKey);
    }
    
    await setDoc(ref, {
      roles: Array.from(roles),
      user_id: user.uid,
      event_id: eventId,
      is_out: false,
      updated_at: serverTimestamp()
    }, { merge: true });
    
    const su = await getDocs(query(collection(db, 'event_signups'), where('event_id', '==', eventId)));
    setEventsSignups(prev => ({ ...prev, [eventId]: su.docs.map(d => ({ id: d.id, ...d.data() })) }));
  };

  // Add a guest (+1) to training
  const addGuest = async (eventId, guestNameInput) => {
    if (!user || !eventId) return;
    const docId = `${eventId}_${user.uid}`;
    const ref = docRef(db, 'event_signups', docId);
    const signups = eventsSignups[eventId] || [];
    const mine = signups.find(s => s.id === docId) || { roles: [], guests: [], user_id: user.uid, event_id: eventId };
    
    // Add guest to array
    const guests = [...(mine.guests || [])];
    guests.push({
      name: guestNameInput || 'Gast',
      added_at: new Date().toISOString()
    });
    
    // Make sure user is also signed up for training
    let roles = new Set(mine.roles || []);
    roles.add('training');
    
    await setDoc(ref, {
      roles: Array.from(roles),
      guests: guests,
      user_id: user.uid,
      event_id: eventId,
      is_out: false,
      updated_at: serverTimestamp()
    }, { merge: true });
    
    const su = await getDocs(query(collection(db, 'event_signups'), where('event_id', '==', eventId)));
    setEventsSignups(prev => ({ ...prev, [eventId]: su.docs.map(d => ({ id: d.id, ...d.data() })) }));
    
    setShowGuestModal(null);
    setGuestName('');
  };

  // Remove a guest from training
  const removeGuest = async (eventId, guestIndex) => {
    if (!user || !eventId) return;
    const docId = `${eventId}_${user.uid}`;
    const ref = docRef(db, 'event_signups', docId);
    const signups = eventsSignups[eventId] || [];
    const mine = signups.find(s => s.id === docId);
    if (!mine) return;
    
    const guests = [...(mine.guests || [])];
    guests.splice(guestIndex, 1);
    
    await setDoc(ref, {
      ...mine,
      guests: guests,
      updated_at: serverTimestamp()
    }, { merge: true });
    
    const su = await getDocs(query(collection(db, 'event_signups'), where('event_id', '==', eventId)));
    setEventsSignups(prev => ({ ...prev, [eventId]: su.docs.map(d => ({ id: d.id, ...d.data() })) }));
  };

  // Load training games for an event (Admin only)
  const loadTrainingGames = async (eventId) => {
    if (role !== 'admin') return;
    try {
      const response = await fetch(`${BACKEND_URL}/api/training/games/by_event/${eventId}`);
      const data = await response.json();
      if (data.ok) {
        setTrainingGames(prev => ({ ...prev, [eventId]: data.games }));
      }
    } catch (err) {
      console.error('Failed to load training games:', err);
    }
  };

  // Delete training game (Admin only)
  const deleteTrainingGame = async (gameId, eventId) => {
    if (role !== 'admin' || !user) return;
    if (!confirm('Spiel wirklich löschen?')) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/training/games/${gameId}?admin_id=${user.uid}`, {
        method: 'DELETE'
      });
      const data = await response.json();
      if (data.ok) {
        setTrainingGames(prev => ({
          ...prev,
          [eventId]: (prev[eventId] || []).filter(g => g.id !== gameId)
        }));
      } else {
        alert(`Fehler: ${data.detail}`);
      }
    } catch (err) {
      alert('Fehler beim Löschen');
    }
  };

  // Delete ALL training games for an event (Admin only)
  const deleteAllTrainingGames = async (eventId) => {
    if (role !== 'admin' || !user) return;
    const games = trainingGames[eventId] || [];
    if (games.length === 0) {
      alert('Keine Spiele zum Löschen');
      return;
    }
    
    if (!window.confirm(`Wirklich alle ${games.length} Spiele für dieses Training löschen?`)) return;
    
    let deletedCount = 0;
    try {
      for (const game of games) {
        const response = await fetch(`${BACKEND_URL}/api/training/games/${game.id}?admin_id=${user.uid}`, {
          method: 'DELETE'
        });
        const result = await response.json();
        if (result.ok) {
          deletedCount++;
        }
      }
      setTrainingGames(prev => ({ ...prev, [eventId]: [] }));
      alert(`✅ ${deletedCount} Spiele gelöscht`);
    } catch (err) {
      console.error('Delete error:', err);
      alert('Fehler beim Löschen');
    }
  };

  // Add a training game directly from Dashboard (Admin only)
  const addTrainingGameDirect = async (eventId, scoreA, scoreB) => {
    if (role !== 'admin' || !user) return;
    
    const sA = parseInt(scoreA);
    const sB = parseInt(scoreB);
    if (isNaN(sA) || isNaN(sB) || sA === sB) {
      alert('Ungültige Punktzahl (kein Unentschieden)');
      return;
    }
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/training/games`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          event_id: eventId,
          team_a_players: [],
          team_b_players: [],
          team_a_score: sA,
          team_b_score: sB
        })
      });
      
      const data = await response.json();
      if (data.ok) {
        // Reload games for this event
        loadTrainingGames(eventId);
      } else {
        alert(`Fehler: ${data.detail}`);
      }
    } catch (err) {
      alert('Fehler beim Speichern');
    }
  };

  // Get guest count for an event
  const getGuestCount = (eventId) => {
    const signups = eventsSignups[eventId] || [];
    return signups.reduce((sum, s) => sum + ((s.guests || []).length), 0);
  };

  // Get my guests for an event
  const getMyGuests = (eventId) => {
    const signups = eventsSignups[eventId] || [];
    const mine = signups.find(s => s.user_id === user?.uid);
    return mine?.guests || [];
  };

  const toggleOut = async (eventId) => {
    if (!user || !eventId) return;
    const docId = `${eventId}_${user.uid}`;
    const ref = docRef(db, 'event_signups', docId);
    const signups = eventsSignups[eventId] || [];
    const mine = signups.find(s => s.id === docId);
    const currentlyOut = mine?.is_out === true;
    
    await setDoc(ref, {
      is_out: !currentlyOut,
      roles: [],
      user_id: user.uid,
      event_id: eventId,
      updated_at: serverTimestamp()
    }, { merge: true });
    
    const su = await getDocs(query(collection(db, 'event_signups'), where('event_id', '==', eventId)));
    setEventsSignups(prev => ({ ...prev, [eventId]: su.docs.map(d => ({ id: d.id, ...d.data() })) }));
  };

  const amIn = (eventId, key) => !!(eventsSignups[eventId]?.find(s => s.user_id === user?.uid && (s.roles || []).includes(key)));
  const amOut = (eventId) => !!(eventsSignups[eventId]?.find(s => s.user_id === user?.uid && s.is_out === true));
  const roleCount = (eventId, key) => (eventsSignups[eventId] || []).filter(s => (s.roles || []).includes(key)).length;

  // Quick Action Cards
  const [marketWin, setMarketWin] = useState(null);
  const [marketTick, setMarketTick] = useState(Date.now());
  const [pendingCoinEvents, setPendingCoinEvents] = useState([]);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        if (!auth.currentUser) return;
        const token = await auth.currentUser.getIdToken();
        const res = await fetch(`${BACKEND_URL}/api/market-ops/window?season_id=2026-27`, { headers: { Authorization: `Bearer ${token}` } });
        if (res.ok && alive) setMarketWin(await res.json());
        const pres = await fetch(`${BACKEND_URL}/api/economy/pending-coin-events`, { headers: { Authorization: `Bearer ${token}` } });
        if (pres.ok && alive) setPendingCoinEvents((await pres.json()).events || []);
      } catch (e) { /* ignore */ }
    })();
    const t = setInterval(() => setMarketTick(Date.now()), 30000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  const quickActions = [
    { icon: Sparkles, label: 'Mein Abend', path: '/mein-abend', color: 'bg-amber-600' },
    { icon: Film, label: 'Media', path: '/media', color: 'bg-fuchsia-600' },
    { icon: CalendarDays, label: 'Kalender', path: '/calendar', color: 'bg-blue-600' },
    { icon: Beer, label: 'Kiosk', path: '/kiosk', color: 'bg-amber-600', badge: activeKioskSession ? '!' : null },
    { icon: BarChart3, label: 'Stats', path: '/stats', color: 'bg-green-600' },
    { icon: MessageSquare, label: 'Chat', path: '/chat', color: 'bg-pink-600', badge: unreadMessages > 0 ? unreadMessages : null },
    { icon: Medal, label: 'Liga', path: '/liga', color: 'bg-indigo-600' },
  ];

  // Grouped under "Sonstiges" to keep the dashboard tidy
  const moreActions = [
    { icon: Trophy, label: 'Turniere', path: '/tournaments', color: 'bg-purple-600' },
    { icon: Gamepad2, label: 'Wetten', path: '/predictions', color: 'bg-orange-600' },
    { icon: BookOpen, label: 'Glossar', path: '/glossar', color: 'bg-teal-600' },
  ];

  return (
    <div className="min-h-screen bg-[#0a0608] text-rose-50 pb-24">
      {/* Header - Compact */}
      <header className="px-4 pt-4 pb-3 bg-gradient-to-b from-[#1a0d11] to-transparent">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold">Hallo, {name.split(' ')[0]}!</h1>
            <p className="text-xs text-rose-200/60">v{APP_VERSION} • {APP_BUILD_DATE}</p>
          </div>
          <div className="flex items-center gap-2">
            {role === 'admin' && (
              <button 
                onClick={() => navigate('/admin')} 
                className="p-2 rounded-lg bg-white/5 hover:bg-white/10"
                data-testid="admin-btn"
              >
                <Settings size={18} />
              </button>
            )}
            <button 
              onClick={logout} 
              className="text-xs px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/15"
              data-testid="logout-btn"
            >
              Logout
            </button>
          </div>
        </div>
      </header>

      <div className="px-3 space-y-3">
        {/* Season Predictions Hero — immer prominent sichtbar */}
        {(() => {
          const accepting = marketWin?.acceptingBets;
          const closes = marketWin?.closesAt ? new Date(marketWin.closesAt) : null;
          let cd = '';
          if (accepting && closes) {
            const ms = Math.max(0, closes.getTime() - marketTick);
            const d = Math.floor(ms / 86400000);
            const h = Math.floor((ms % 86400000) / 3600000);
            const m = Math.floor((ms % 3600000) / 60000);
            cd = d > 0 ? `Noch ${d} Tage ${h} Std` : (h > 0 ? `Noch ${h} Std ${m} Min` : `Noch ${m} Min`);
          }
          return (
            <button
              onClick={() => navigate('/predictions')}
              data-testid="dashboard-season-hero"
              className="w-full text-left rounded-2xl p-5 border border-orange-500/40 bg-gradient-to-br from-orange-600/40 via-rose-700/25 to-purple-800/20 hover:from-orange-600/50 transition-colors relative overflow-hidden"
            >
              <div className="absolute -right-6 -top-6 opacity-20">
                <Flame size={120} className="text-orange-300" />
              </div>
              <div className="relative flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 text-orange-200 text-xs font-semibold tracking-wide uppercase">
                    <Flame size={14} /> Season Predictions 2026/27
                  </div>
                  <h2 className="text-2xl font-black text-white mt-1 leading-tight">
                    {accepting ? 'Wettfenster offen 🔥' : 'Wetten & Ranking'}
                  </h2>
                  <p className="text-sm text-orange-100/80 mt-0.5">
                    {accepting ? `${cd} · jetzt auf die Predictions setzen` : 'Setze deine Coins ein und klettere im Ranking'}
                  </p>
                </div>
                <span className="shrink-0 text-sm font-bold px-5 py-2.5 rounded-full bg-orange-500 text-black shadow-lg" data-testid="dashboard-market-bet-btn">
                  {accepting ? 'Jetzt wetten →' : 'Öffnen →'}
                </span>
              </div>
            </button>
          );
        })()}

        {unreadMessages > 0 && (
          <Card 
            className="bg-gradient-to-r from-pink-900/40 to-rose-900/40 border-pink-500/30 cursor-pointer"
            onClick={() => navigate('/chat')}
            data-testid="chat-alert"
          >
            <CardContent className="py-3 px-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <MessageSquare size={24} className="text-pink-400" />
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-bold animate-pulse">
                      {unreadMessages}
                    </span>
                  </div>
                  <div>
                    <p className="font-semibold text-pink-200">Neue Nachrichten!</p>
                    <p className="text-xs text-pink-200/70">
                      {unreadMessages} ungelesene {unreadMessages === 1 ? 'Nachricht' : 'Nachrichten'}
                    </p>
                  </div>
                </div>
                <ChevronRight className="text-pink-400" />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Offene Beteiligungs-Coins (bleiben bis bestätigt) */}
        {pendingCoinEvents.length > 0 && (
          <Card className="bg-gradient-to-r from-amber-900/30 to-yellow-900/20 border-amber-500/30" data-testid="pending-coin-events">
            <CardContent className="py-3 px-4 space-y-2">
              <div className="flex items-center gap-2">
                <span className="text-xl">🪙</span>
                <div className="flex-1">
                  <p className="font-semibold text-amber-200 text-sm">Coins zu vergeben — noch unbestätigt</p>
                  <p className="text-[11px] text-amber-200/70">Bestätige, wer dabei war & Aufgaben übernahm (Training 15 · Trikots 25 · Tisch 35 · Coach 30 · Bälle/Schlüssel 15).</p>
                </div>
              </div>
              <div className="space-y-1.5">
                {pendingCoinEvents.slice(0, 5).map((e) => {
                  const d = new Date(e.start);
                  return (
                    <button
                      key={e.eventId}
                      onClick={() => navigate('/calendar')}
                      className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg bg-black/20 hover:bg-black/30 transition-colors text-left"
                      data-testid={`pending-event-${e.eventId}`}
                    >
                      <div className="min-w-0">
                        <span className="text-sm text-rose-50 truncate block">
                          {e.type === 'training' ? '🏀' : '🆚'} {e.title}
                        </span>
                        <span className="text-[11px] text-white/50">
                          {d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })} · {e.peopleCount} {e.peopleCount === 1 ? 'Person' : 'Personen'}
                        </span>
                      </div>
                      <span className="shrink-0 text-xs font-bold text-amber-300 flex items-center gap-1">
                        +{e.pendingCoins} <ChevronRight size={14} className="text-amber-400/60" />
                      </span>
                    </button>
                  );
                })}
                {pendingCoinEvents.length > 5 && (
                  <button onClick={() => navigate('/calendar')} className="w-full text-[11px] text-amber-300/70 hover:text-amber-300 py-1" data-testid="pending-events-more">
                    +{pendingCoinEvents.length - 5} weitere im Kalender →
                  </button>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Notification & Install Row */}
        <div className="flex gap-2">
          <button
            onClick={toggleNotifications}
            className={`flex-1 p-3 rounded-xl flex items-center justify-center gap-2 transition-colors ${
              notificationsEnabled 
                ? 'bg-green-600/20 border border-green-500/30 text-green-300' 
                : 'bg-white/5 border border-white/10 text-rose-200/60 hover:bg-white/10'
            }`}
            data-testid="notification-toggle"
          >
            {notificationLoading ? (
              <Loader2 size={18} className="animate-spin" />
            ) : notificationsEnabled ? (
              <Bell size={18} />
            ) : (
              <BellOff size={18} />
            )}
            <span className="text-xs font-medium">
              {notificationLoading ? 'Aktiviere...' : notificationsEnabled ? 'Push aktiv ✓' : 'Push aktivieren'}
            </span>
          </button>
          
          {notificationsEnabled && (
            <button
              onClick={testPushNotification}
              className="p-3 rounded-xl flex items-center justify-center gap-2 bg-yellow-600/20 border border-yellow-500/30 text-yellow-300 hover:bg-yellow-600/30 transition-colors"
              data-testid="test-push-btn"
            >
              <Bell size={18} />
              <span className="text-xs font-medium">Test</span>
            </button>
          )}
          
          <button
            onClick={installChatShortcut}
            className="flex-1 p-3 rounded-xl flex items-center justify-center gap-2 bg-pink-600/20 border border-pink-500/30 text-pink-300 hover:bg-pink-600/30 transition-colors"
            data-testid="install-chat-btn"
          >
            <Download size={18} />
            <span className="text-xs font-medium">Chat-Shortcut</span>
          </button>
        </div>

        {/* Kiosk Alert */}
        {activeKioskSession && (
          <Card 
            className="bg-gradient-to-r from-amber-900/40 to-orange-900/40 border-amber-500/30 cursor-pointer"
            onClick={() => navigate('/kiosk')}
            data-testid="kiosk-alert"
          >
            <CardContent className="py-3 px-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-2xl animate-bounce">🍺</span>
                  <div>
                    <p className="font-semibold text-amber-200">Kiosk aktiv!</p>
                    <p className="text-xs text-amber-200/70">
                      {activeKioskSession.user_ids?.length || 0} Spieler • {activeKioskSession.location_name || 'Standort'}
                    </p>
                  </div>
                </div>
                <ChevronRight className="text-amber-400" />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Quick Actions Grid */}
        <div className="grid grid-cols-3 gap-2" data-testid="quick-actions">
          {quickActions.map((action) => (
            <button
              key={action.path}
              onClick={() => navigate(action.path)}
              className={`${action.color} relative p-3 rounded-xl flex flex-col items-center gap-1.5 hover:opacity-90 transition-opacity`}
              data-testid={`action-${action.label.toLowerCase()}`}
            >
              <action.icon size={20} />
              <span className="text-xs font-medium">{action.label}</span>
              {action.badge && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-bold">
                  {action.badge}
                </span>
              )}
            </button>
          ))}

          {/* Sonstiges — bündelt Turniere, Wetten & Glossar */}
          <button
            onClick={() => setShowMore(v => !v)}
            className={`relative p-3 rounded-xl flex flex-col items-center gap-1.5 transition-colors ${showMore ? 'bg-slate-600' : 'bg-slate-700 hover:bg-slate-600'}`}
            data-testid="action-sonstiges"
          >
            <MoreHorizontal size={20} />
            <span className="text-xs font-medium">Sonstiges</span>
            <ChevronRight size={12} className={`absolute top-2 right-2 text-white/50 transition-transform ${showMore ? 'rotate-90' : ''}`} />
          </button>
        </div>

        {/* Sonstiges expanded */}
        {showMore && (
          <div className="grid grid-cols-3 gap-2" data-testid="more-actions">
            {moreActions.map((action) => (
              <button
                key={action.path}
                onClick={() => navigate(action.path)}
                className={`${action.color} relative p-3 rounded-xl flex flex-col items-center gap-1.5 hover:opacity-90 transition-opacity`}
                data-testid={`action-${action.label.toLowerCase()}`}
              >
                <action.icon size={20} />
                <span className="text-xs font-medium">{action.label}</span>
              </button>
            ))}
          </div>
        )}

        {/* Next Events */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-sm font-semibold text-rose-200/80">Nächste Events</h2>
            <button onClick={() => navigate('/calendar')} className="text-xs text-rose-200/50 hover:text-rose-200">
              Alle →
            </button>
          </div>

          {nextEvents.length === 0 ? (
            <Card className="bg-[#12090c] border-white/5">
              <CardContent className="py-4 text-center text-rose-200/50 text-sm">
                Keine anstehenden Events
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {nextEvents.map((ev, idx) => {
                const signups = eventsSignups[ev.id] || [];
                const isOut = amOut(ev.id);
                // Count players + their guests
                const playersIn = signups.filter(s => (s.roles || []).length > 0);
                const guestCount = playersIn.reduce((sum, s) => sum + (s.guests || []).length, 0);
                const totalIn = playersIn.length + guestCount;
                const totalOut = signups.filter(s => s.is_out).length;
                const isExpanded = expandedEvent === ev.id;
                const isTraining = ev.type === 'training';

                return (
                  <Card key={ev.id} className="bg-[#12090c] border-white/5 overflow-hidden" data-testid="event-card">
                    <CardContent className="p-0">
                      {/* Event Header */}
                      <div 
                        className="p-3 cursor-pointer hover:bg-white/5 transition-colors"
                        onClick={() => setExpandedEvent(isExpanded ? null : ev.id)}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className={`w-2 h-2 rounded-full ${isTraining ? 'bg-blue-500' : 'bg-red-500'}`} />
                              <p className="font-medium text-sm truncate">
                                {ev.title || (isTraining ? 'Training' : 'Spiel')}
                              </p>
                            </div>
                            <div className="flex items-center gap-3 mt-1 text-xs text-rose-200/50">
                              <span className="flex items-center gap-1">
                                <Clock size={12} />
                                {ev._dt?.toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit' })} • {ev._dt?.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}
                              </span>
                              <span className="flex items-center gap-1">
                                <MapPin size={12} />
                                {ev.gym_name || 'TBA'}
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 ml-2">
                            <div className="flex items-center gap-1.5 text-xs">
                              <span className="flex items-center gap-0.5 text-green-400">
                                <Check size={12} className="text-green-400" />
                                {totalIn}
                              </span>
                              {totalOut > 0 && (
                                <span className="flex items-center gap-0.5 text-red-400">
                                  <X size={12} className="text-red-400" />
                                  {totalOut}
                                </span>
                              )}
                            </div>
                            <ChevronRight size={16} className={`text-rose-200/30 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                          </div>
                        </div>
                      </div>

                      {/* Expanded Content */}
                      {isExpanded && (
                        <div className="px-3 pb-3 border-t border-white/5 pt-3 space-y-3">
                          {/* Role Buttons */}
                          <div className={`grid gap-1.5 ${isTraining ? 'grid-cols-2' : 'grid-cols-2'}`}>
                            {isTraining ? (
                              <>
                                <button
                                  onClick={() => toggleRole(ev.id, 'training')}
                                  disabled={isOut}
                                  className={`p-2 rounded-lg text-xs font-medium transition-colors flex items-center justify-center gap-2 ${
                                    amIn(ev.id, 'training')
                                      ? 'bg-green-600/30 border border-green-500/50 text-green-300'
                                      : 'bg-white/5 border border-white/10 text-rose-200/70 hover:bg-white/10'
                                  } ${isOut ? 'opacity-50' : ''}`}
                                  data-testid="training-signup-btn"
                                >
                                  <CheckCircle2 size={14} />
                                  Ich komme ({roleCount(ev.id, 'training')})
                                </button>
                                <button
                                  onClick={() => setShowGuestModal(ev.id)}
                                  disabled={isOut}
                                  className={`p-2 rounded-lg text-xs font-medium transition-colors flex items-center justify-center gap-2 bg-blue-600/20 border border-blue-500/30 text-blue-300 hover:bg-blue-600/30 ${isOut ? 'opacity-50' : ''}`}
                                  data-testid="add-guest-btn"
                                >
                                  <Users size={14} />
                                  +1 Gast ({getGuestCount(ev.id)})
                                </button>
                              </>
                            ) : (
                              GAME_ROLES.filter(r => {
                                if (r.homeOnly && !ev.team_home) return false;
                                if (r.awayOnly && ev.team_home) return false;
                                return true;
                              }).map(r => {
                                const count = roleCount(ev.id, r.key);
                                const isFull = r.max && count >= r.max && !amIn(ev.id, r.key);
                                return (
                                  <button
                                    key={r.key}
                                    onClick={() => toggleRole(ev.id, r.key)}
                                    disabled={isOut || isFull}
                                    className={`p-2 rounded-lg text-xs font-medium transition-colors flex items-center justify-between ${
                                      amIn(ev.id, r.key)
                                        ? 'bg-green-600/30 border border-green-500/50 text-green-300'
                                        : isFull
                                          ? 'bg-white/5 border border-white/5 text-rose-200/30'
                                          : 'bg-white/5 border border-white/10 text-rose-200/70 hover:bg-white/10'
                                    } ${isOut ? 'opacity-50' : ''}`}
                                  >
                                    <span>{r.icon} {r.label}</span>
                                    <span className="text-rose-200/40">
                                      {count}{r.max ? `/${r.max}` : ''}
                                    </span>
                                  </button>
                                );
                              })
                            )}
                          </div>

                          {/* Cancel Button */}
                          <button
                            onClick={() => toggleOut(ev.id)}
                            className={`w-full p-2 rounded-lg text-xs font-medium transition-colors flex items-center justify-center gap-2 ${
                              isOut
                                ? 'bg-red-600/30 border border-red-500/50 text-red-300'
                                : 'bg-white/5 border border-white/10 text-rose-200/50 hover:bg-red-900/20 hover:border-red-500/30'
                            }`}
                          >
                            <XCircle size={14} />
                            {isOut ? 'Abgesagt ✓' : 'Bin raus'}
                          </button>

                          {/* Team Builder Button (Training only) */}
                          {isTraining && totalIn >= 4 && (
                            <button
                              onClick={() => setShowTeamBuilder(ev.id)}
                              className="w-full p-2 rounded-lg text-xs font-medium transition-colors flex items-center justify-center gap-2 bg-gradient-to-r from-orange-600/30 to-red-600/30 border border-orange-500/30 text-orange-300 hover:from-orange-600/40 hover:to-red-600/40"
                              data-testid="team-builder-btn"
                            >
                              <Shuffle size={14} />
                              Teams bilden ({totalIn >= 13 ? '3' : '2'} Teams aus {totalIn} Spielern)
                            </button>
                          )}

                          {/* Participants Preview */}
                          {totalIn > 0 && (
                            <div className="pt-2 border-t border-white/5">
                              <p className="text-xs text-rose-200/40 mb-1">Dabei:</p>
                              <div className="flex flex-wrap gap-1">
                                {signups.filter(s => (s.roles || []).length > 0).slice(0, 8).map(s => (
                                  <span key={s.user_id} className="text-xs px-2 py-0.5 rounded-full bg-white/5 text-rose-200/60">
                                    {getShortUserName(s.user_id)}
                                    {/* Show guest count badge */}
                                    {(s.guests || []).length > 0 && (
                                      <span className="ml-1 text-blue-400">+{(s.guests || []).length}</span>
                                    )}
                                  </span>
                                ))}
                                {totalIn > 8 && (
                                  <span className="text-xs px-2 py-0.5 text-rose-200/40">+{totalIn - 8}</span>
                                )}
                              </div>
                            </div>
                          )}

                          {/* My Guests (only for training) */}
                          {isTraining && getMyGuests(ev.id).length > 0 && (
                            <div className="pt-2 border-t border-white/5">
                              <p className="text-xs text-rose-200/40 mb-1">Meine Gäste:</p>
                              <div className="flex flex-wrap gap-1">
                                {getMyGuests(ev.id).map((guest, idx) => (
                                  <span 
                                    key={idx} 
                                    className="text-xs px-2 py-0.5 rounded-full bg-blue-600/20 border border-blue-500/30 text-blue-300 flex items-center gap-1"
                                  >
                                    {guest.name || 'Gast'}
                                    <button 
                                      onClick={(e) => { e.stopPropagation(); removeGuest(ev.id, idx); }}
                                      className="ml-1 hover:text-red-400"
                                      data-testid={`remove-guest-${idx}`}
                                    >
                                      ✕
                                    </button>
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Admin: Training Games (only for training) */}
                          {isTraining && role === 'admin' && (
                            <div className="pt-2 border-t border-white/5">
                              <div className="flex items-center justify-between mb-2">
                                <button 
                                  onClick={(e) => { e.stopPropagation(); loadTrainingGames(ev.id); }}
                                  className="text-xs text-orange-400 hover:text-orange-300"
                                >
                                  📊 Spiele laden
                                </button>
                                {(trainingGames[ev.id] || []).length > 0 && (
                                  <button 
                                    onClick={(e) => { e.stopPropagation(); deleteAllTrainingGames(ev.id); }}
                                    className="text-xs text-red-400 hover:text-red-300"
                                  >
                                    🗑️ Alle
                                  </button>
                                )}
                              </div>
                              
                              {/* Games List */}
                              {(trainingGames[ev.id] || []).length > 0 && (
                                <div className="space-y-1 mb-2">
                                  {trainingGames[ev.id].map((game) => (
                                    <div key={game.id} className="flex items-center justify-between bg-white/5 rounded p-1.5 text-xs">
                                      <span className="text-blue-300">Blau</span>
                                      <span className="text-white font-bold">{game.team_a_score}:{game.team_b_score}</span>
                                      <span className="text-red-300">Rot</span>
                                      <span className="text-yellow-400 text-[10px]">
                                        {game.winner === 'A' ? '🏆' : ''}
                                      </span>
                                      <span className="text-yellow-400 text-[10px]">
                                        {game.winner === 'B' ? '🏆' : ''}
                                      </span>
                                      <button 
                                        onClick={(e) => { e.stopPropagation(); deleteTrainingGame(game.id, ev.id); }}
                                        className="text-red-400 hover:text-red-300 ml-2"
                                        title="Spiel löschen"
                                      >
                                        ✕
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              )}
                              
                              {/* Add New Game Form (Admin) */}
                              <div className="bg-black/20 rounded p-2 mt-2">
                                <div className="text-xs text-rose-200/50 mb-1">Neues Spiel:</div>
                                <div className="flex items-center gap-1">
                                  <input 
                                    type="number" 
                                    min="0" 
                                    placeholder="Blau"
                                    className="w-12 p-1 text-xs rounded bg-blue-900/30 border border-blue-500/30 text-white text-center"
                                    id={`new-game-a-${ev.id}`}
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                  <span className="text-white">:</span>
                                  <input 
                                    type="number" 
                                    min="0" 
                                    placeholder="Rot"
                                    className="w-12 p-1 text-xs rounded bg-red-900/30 border border-red-500/30 text-white text-center"
                                    id={`new-game-b-${ev.id}`}
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                  <button 
                                    onClick={(e) => { 
                                      e.stopPropagation(); 
                                      const scoreA = document.getElementById(`new-game-a-${ev.id}`).value;
                                      const scoreB = document.getElementById(`new-game-b-${ev.id}`).value;
                                      if (scoreA && scoreB) {
                                        addTrainingGameDirect(ev.id, scoreA, scoreB);
                                        document.getElementById(`new-game-a-${ev.id}`).value = '';
                                        document.getElementById(`new-game-b-${ev.id}`).value = '';
                                      }
                                    }}
                                    className="text-xs px-2 py-1 rounded bg-green-600/50 text-green-200 hover:bg-green-600"
                                  >
                                    +
                                  </button>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>

        {/* Admin Quick Links */}
        {role === 'admin' && (
          <Card className="bg-[#12090c] border-white/5">
            <CardContent className="py-3 px-4">
              <p className="text-xs font-semibold text-rose-200/50 mb-2">Admin</p>
              <div className="flex flex-wrap gap-2">
                <button onClick={() => navigate('/admin/users')} className="text-xs px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10">
                  👥 User
                </button>
                <button onClick={() => navigate('/admin/predictions')} className="text-xs px-3 py-1.5 rounded-lg bg-gradient-to-r from-purple-600/30 to-pink-600/30 hover:from-purple-600/50 hover:to-pink-600/50 border border-purple-500/30">
                  🎯 Predictions
                </button>
                <button onClick={() => navigate('/admin/market-ops')} className="text-xs px-3 py-1.5 rounded-lg bg-gradient-to-r from-orange-600/30 to-red-600/30 hover:from-orange-600/50 hover:to-red-600/50 border border-orange-500/30" data-testid="admin-market-ops-link">
                  🔥 Markt &amp; Coins
                </button>
                <button onClick={() => navigate('/stats-import')} className="text-xs px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10">
                  📊 Stats Import
                </button>
                <button onClick={() => navigate('/events-import')} className="text-xs px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10">
                  📅 Events Import
                </button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Install Chat Modal */}
      {showInstallModal && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setShowInstallModal(false)}>
          <div 
            className="bg-[#12090c] border border-pink-500/30 rounded-xl p-4 w-full max-w-sm max-h-[80vh] overflow-y-auto"
            onClick={e => e.stopPropagation()}
          >
            <h3 className="font-semibold text-pink-300 mb-3">Chat-Shortcut erstellen</h3>
            <pre className="text-sm text-rose-200/80 whitespace-pre-wrap font-sans leading-relaxed">
              {installInstructions}
            </pre>
            
            {/* Button to open chat URL externally */}
            <a 
              href={chatUrlForModal}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full mt-4 py-3 bg-gradient-to-r from-pink-600 to-rose-600 hover:from-pink-700 hover:to-rose-700 rounded-lg font-medium flex items-center justify-center gap-2 text-white"
            >
              <Download size={18} />
              Chat öffnen (extern)
            </a>
            
            <button 
              onClick={() => setShowInstallModal(false)}
              className="w-full mt-2 py-2 bg-white/10 hover:bg-white/20 rounded-lg font-medium text-rose-200/70"
            >
              Schließen
            </button>
          </div>
        </div>
      )}

      {/* Guest Modal */}
      {showGuestModal && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => { setShowGuestModal(null); setGuestName(''); }}>
          <div 
            className="bg-[#12090c] border border-blue-500/30 rounded-xl p-4 w-full max-w-sm"
            onClick={e => e.stopPropagation()}
          >
            <h3 className="font-semibold text-blue-300 mb-3">+1 Gast hinzufügen</h3>
            <p className="text-sm text-rose-200/60 mb-4">
              Bringe einen Freund zum Training mit! Optional: Name eingeben.
            </p>
            
            <input
              type="text"
              placeholder="Name des Gastes (optional)"
              value={guestName}
              onChange={(e) => setGuestName(e.target.value)}
              className="w-full p-3 rounded-lg bg-white/5 border border-white/10 text-rose-50 placeholder-rose-200/40 focus:outline-none focus:border-blue-500/50 mb-4"
              data-testid="guest-name-input"
              autoFocus
            />
            
            <div className="flex gap-2">
              <button 
                onClick={() => { setShowGuestModal(null); setGuestName(''); }}
                className="flex-1 py-2 bg-white/10 hover:bg-white/20 rounded-lg font-medium text-rose-200/70"
              >
                Abbrechen
              </button>
              <button 
                onClick={() => addGuest(showGuestModal, guestName)}
                className="flex-1 py-2 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 rounded-lg font-medium text-white"
                data-testid="confirm-add-guest-btn"
              >
                Gast hinzufügen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Team Builder Modal */}
      {showTeamBuilder && (
        <TeamBuilder
          eventId={showTeamBuilder}
          signedUpUsers={eventsSignups[showTeamBuilder] || []}
          allUsers={users}
          isAdmin={role === 'admin'}
          onClose={() => setShowTeamBuilder(null)}
        />
      )}
    </div>
  );
}
